import { expVal } from '@confluence/feature-experiments';

import type { SmartLinkType } from './getSmartLinks.ts';
import { RESOLVED, FORBIDDEN } from './status.ts';

const JIRA_OBJECT_PROVIDER = 'jira-object-provider';

/**
 * Whether a GraphQL-resolved link can skip ORS entirely.
 */
const canBypassORS = (link: SmartLinkType): boolean => {
	if (link.resolved !== true) {
		return false;
	}

	if (hasUrlFragment(link.resourceUrl)) {
		return false;
	}

	switch (link.product) {
		case 'confluence':
			return true;
		case 'jira':
			return expVal('confluence_skip_ors_for_jira_smartlinks', 'isEnabled', false);
		default:
			return false;
	}
};

/**
 * Partitions smart links into those resolved by GraphQL (Confluence, and Jira once the
 * experiment is enabled)
 * and those that still need to be resolved via ORS.
 */
export const partitionResolvedLinks = (
	links: SmartLinkType[],
): { resolvedFromGql: SmartLinkType[]; linksForORS: SmartLinkType[] } => {
	const resolvedFromGql: SmartLinkType[] = [];
	const linksForORS: SmartLinkType[] = [];

	for (const link of links) {
		if (canBypassORS(link)) {
			resolvedFromGql.push(link);
		} else {
			linksForORS.push(link);
		}
	}

	return { resolvedFromGql, linksForORS };
};

export function hasUrlFragment(url?: string | null): boolean {
	if (!url) {
		return false;
	}

	try {
		return new URL(url).hash.length > 1;
	} catch {
		const [, fragment] = url.split('#');
		return Boolean(fragment);
	}
}

/**
 * Converts SmartLinks that were resolved via GraphQL directly into the
 * SmartCard Provider initial state format, matching the shape produced by
 * getInitialState (which processes ORS responses).
 *
 * This allows resolved links to bypass the ORS call entirely while still
 * producing a compatible state shape for the SmartCard Provider stored
 * in window.__SSR_SMARTLINKS__.
 *
 * NOTE: Only Confluence and Jira links are supported. Links from any other product
 * should continue to be resolved via ORS — see partitionResolvedLinks.
 *
 * The shape produced is:
 *   { [url]: { status: string, details: { meta: {...}, data: { url, name, ... } } } }
 */
export const buildResolvedSmartLinkState = (
	resolvedLinks: SmartLinkType[],
): Record<string, any> => {
	if (!resolvedLinks?.length) {
		return {};
	}

	return resolvedLinks.reduce(
		(map, link) => {
			const url = link.resourceUrl;
			if (!url) {
				return map;
			}

			const cardState = buildCardStateForProduct(link, url);
			// Unknown product: omit it so the client resolves it through ORS as usual, rather
			// than hydrating a card built from the wrong product's shape.
			if (!cardState) {
				return map;
			}

			return {
				...map,
				[url]: cardState,
			};
		},
		{} as Record<string, any>,
	);
};

const buildCardStateForProduct = (link: SmartLinkType, url: string) => {
	switch (link.product) {
		case 'confluence':
			return buildConfluenceCardState(link, url);
		case 'jira':
			return buildJiraCardState(link, url);
		default:
			return undefined;
	}
};

/**
 * Builds the Confluence card state. Mirrors the ORS response for
 * integrations-confluence-object-provider / canvas-native-object-provider.
 */
const buildConfluenceCardState = (link: SmartLinkType, url: string) => {
	const iconUrl = getStaticIconUrl(link.resourceType, url) ?? link.iconUrl;
	return {
		status: link.canView === true ? RESOLVED : FORBIDDEN,
		details: {
			meta: {
				access: link.canView === true ? 'granted' : 'forbidden',
				auth: [],
				definitionId: getDefinitionId(link.resourceType),
				resourceType: link.resourceType,
				product: 'confluence',
			},
			data: {
				'@context': {
					'@vocab': 'https://www.w3.org/ns/activitystreams#',
					atlassian: 'https://schema.atlassian.com/ns/vocabulary#',
					schema: 'https://schema.org/',
				},
				'@type': getResourceType(link.resourceType, link.subType),
				name: link.title || '',
				url,
				...(hasTopLevelIcon(link.resourceType) && iconUrl
					? { icon: { '@type': 'Image', url: iconUrl } }
					: {}),
				...(hasTitlePrefix(link.resourceType) && link.emojiTitle
					? {
							'atlassian:titlePrefix': {
								'@type': 'atlassian:Emoji',
								text: link.emojiTitle,
							},
						}
					: {}),
				generator: {
					'@type': 'Application',
					'@id': getGeneratorId(link.resourceType),
					name: getGeneratorName(link.resourceType),
					...(iconUrl ? { icon: { '@type': 'Image', url: iconUrl } } : {}),
				},
			},
		},
	};
};

/**
 * Builds the Jira card state. Mirrors the ORS response for jira-object-provider.
 */
const buildJiraCardState = (link: SmartLinkType, url: string) => ({
	status: link.canView === true ? RESOLVED : FORBIDDEN,
	details: {
		meta: {
			access: link.canView === true ? 'granted' : 'forbidden',
			auth: [],
			definitionId: JIRA_OBJECT_PROVIDER,
			key: JIRA_OBJECT_PROVIDER,
			is1PLink: true,
			product: 'jira',
			resourceType: link.resourceType,
			// resourceSubType is the Jira issue type display name (Story, Bug, Task, ...)
			resourceSubType: link.subType,
		},
		data: {
			'@context': {
				'@vocab': 'https://www.w3.org/ns/activitystreams#',
				atlassian: 'https://schema.atlassian.com/ns/vocabulary#',
				schema: 'http://schema.org/',
			},
			'@type': ['atlassian:Task', 'Object'],
			name: link.title || '',
			url,
			...(link.iconUrl ? { icon: { '@type': 'Image', url: link.iconUrl } } : {}),
			...(link.statusText
				? {
						tag: {
							'@type': 'Object',
							name: link.statusText,
							...(link.statusAppearance ? { appearance: link.statusAppearance } : {}),
						},
					}
				: {}),
			generator: {
				'@type': 'Application',
				'@id': 'https://www.atlassian.com/#Jira',
				name: 'Jira',
			},
		},
	},
});

/**
 * Map resourceType to the ORS definitionId.
 * Whiteboards and databases use a different provider than pages/blogs/spaces.
 */
function getDefinitionId(resourceType?: string | null): string {
	switch (resourceType) {
		case 'whiteboard':
		case 'database':
		case 'slide':
			return 'canvas-native-object-provider';
		default:
			return 'integrations-confluence-object-provider';
	}
}

/**
 * Whether this resourceType has a top-level `icon` field in the ORS data response.
 * Pages and blogs do not; whiteboards, databases, slides, folders, and spaces do.
 */
function hasTopLevelIcon(resourceType?: string | null): boolean {
	switch (resourceType) {
		case 'whiteboard':
		case 'database':
		case 'slide':
		case 'folder':
		case 'space':
			return true;
		default:
			return false;
	}
}

/**
 * Returns a hardcoded icon URL for resource types that do not have an icon URL returned by the
 * GraphQL API. Whiteboards and databases fall into this category.
 * These URL must match those listed in https://bitbucket.org/atlassian/canvas/src/68e87076d0168bb32184f72e66cea5cfc0a96516/services/canvas-server/canvas-server/src/routes/private/smart-link/smart-link-utils.ts
 * For database links with an `entryId` query parameter, a database-entry icon is used instead.
 */
function getStaticIconUrl(resourceType?: string | null, url?: string | null): string | null {
	switch (resourceType) {
		case 'whiteboard':
			return 'https://canvas-frontend.prod-east.frontend.public.atl-paas.net/assets/whiteboard-logo.svg';
		case 'database': {
			if (url) {
				try {
					const parsedUrl = new URL(url);
					if (parsedUrl.searchParams.has('entryId')) {
						return 'https://canvas-frontend.prod-east.frontend.public.atl-paas.net/assets/database-entry-logo.svg';
					}
				} catch {
					// If URL parsing fails, fall through to the default database icon
				}
			}
			return 'https://canvas-frontend.prod-east.frontend.public.atl-paas.net/assets/database-logo.svg';
		}
		case 'slide':
			return 'https://canvas-frontend.prod-east.frontend.public.atl-paas.net/assets/slides-logo.svg';
		case 'folder':
			return 'https://dam-cdn.atl.orangelogic.com/CDNLink/AT1315J.svg';
		default:
			return null;
	}
}

/**
 * Whether this resourceType includes `atlassian:titlePrefix` in the ORS data response.
 * Pages, blogs, and spaces do; whiteboards and databases do not.
 */
function hasTitlePrefix(resourceType?: string | null): boolean {
	switch (resourceType) {
		case 'whiteboard':
		case 'database':
		case 'slide':
			return false;
		default:
			return true;
	}
}

/**
 * Map resourceType to the generator @id used by the SmartCard Provider.
 * Values are based on actual ORS responses.
 */
function getGeneratorId(resourceType?: string | null): string {
	switch (resourceType) {
		case 'whiteboard':
			return 'https://www.atlassian.com/#ConfluenceWhiteboards';
		case 'database':
			return 'https://www.atlassian.com/#ConfluenceDatabases';
		case 'slide':
			return 'https://www.atlassian.com/#ConfluenceSlides';
		default:
			return 'https://www.atlassian.com/#Confluence';
	}
}

/**
 * Map resourceType to the generator display name.
 * Values are based on actual ORS responses.
 */
function getGeneratorName(resourceType?: string | null): string {
	switch (resourceType) {
		case 'whiteboard':
			return 'Confluence Whiteboards';
		case 'database':
			return 'Confluence Databases';
		case 'slide':
			return 'Confluence Slides';
		default:
			return 'Confluence';
	}
}

/**
 * Map resourceType to an ActivityStreams @type value for Confluence content.
 * Values are based on actual ORS responses.
 */
function getResourceType(resourceType?: string | null, subType?: string | null): string | string[] {
	switch (resourceType) {
		case 'blogpost':
			return ['Document', 'schema:BlogPosting'];
		case 'page':
			if (subType === 'live') {
				return ['Document', 'schema:DigitalDocument'];
			}
			return ['Document', 'schema:TextDigitalDocument'];
		case 'whiteboard':
		case 'database':
		case 'slide':
			return 'Document';
		case 'space':
			return ['Object', 'atlassian:Project'];
		default:
			return ['Document', 'schema:TextDigitalDocument'];
	}
}
